import { SectionLayout } from "@/components/marketing/SectionLayout";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Btn, Eyebrow, SectionHeader } from "@/components/marketing/primitives";
import { PartnerBadges } from "@/components/marketing/PartnerBadges";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { TrustRibbon } from "@/components/marketing/TrustRibbon";
import { FAQSection } from "@/components/marketing/FAQSection";
import { ABOUT_FAQS, faqJsonLd } from "@/content/faqs";
import { OG_IMAGE, abs } from "@/content/siteConfig";
import { pushAuditStart } from "@/lib/analytics";


export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Berk Hospitality Group | Home Services Marketing, Scottsdale" },
      { name: "description", content: "Berk Hospitality Group is a Scottsdale marketing agency for home services: Google and Meta ads, SEO, AI search and automation. Google Partner, bilingual EN/ES." },
      { property: "og:title", content: "About Berk Hospitality Group | Home Services Marketing, Scottsdale" },
      { property: "og:description", content: "Berk Hospitality Group is a Scottsdale marketing agency for home services: Google and Meta ads, SEO, AI search and automation. Google Partner, bilingual EN/ES." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Berk Hospitality Group | Paid Media Agency, Scottsdale AZ" },
      { name: "twitter:description", content: "Scottsdale paid media agency running Google Ads, Meta, Local Services Ads and YouTube for home services, local businesses and consumer brands. Google Partner, bilingual EN/ES." },
      { property: "og:url", content: abs("/about") },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: abs("/about") }],
    scripts: [{ type: "application/ld+json", children: faqJsonLd(ABOUT_FAQS) }],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="About"
        lede={
          <>
            We&apos;re a paid-media firm built for operators who measure on outcomes, from service businesses and trades to consumer brands. Scottsdale-based, bilingual,
            and certified by the platforms we run.
          </>
        }
        ctas={<Btn to="/audit" onClick={() => pushAuditStart("inline")}>Get your free audit →</Btn>}

      >
        Paid media, built for <span className="display-italic">businesses that scale.</span>
      </PageHero>

      <TrustRibbon />


      <section className="bhg-section">
        <SectionLayout className="section-eyebrow-only">
          <div className="eyebrow-lead">
            <Eyebrow>Our approach</Eyebrow>
            <div>
              <p
                className="prose-lead"
                style={{
                  fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                  fontWeight: 300,
                  fontSize: "clamp(20px, 2.2vw, 28px)",
                  lineHeight: 1.35,
                  color: "#002FA7",
                  margin: 0,
                  maxWidth: "52ch",
                }}
              >
                Paid media should be run as an asset, not a campaign: accounts that compound, not content packages or hourly retainers.
              </p>
              <p
                className="mt-6"
                style={{ color: "rgba(0,47,167,0.74)", fontSize: 16, lineHeight: 1.55, maxWidth: "60ch" }}
              >
                The model holds for service businesses, multi-location operators and consumer brands alike. Lead quality beats volume, the buyer journey splits across Google, Meta, LSA and YouTube in ways generalists rarely model, and weekly cadence beats quarterly check-ins. And because a lead is only worth something if it gets answered, we also build the AI and automation behind the ads.
              </p>
            </div>
          </div>
        </SectionLayout>
      </section>

      {/* WHY OUR OPERATING MODEL */}
      <section className="bhg-section" style={{ background: "#f3f1e9" }}>
        <SectionLayout>
          <SectionHeader
            num="02, Operating model"
            title={<>What we&apos;ve <span className="display-italic">internalized.</span></>}
          >
            Pattern recognition compounds when an operating model is applied with discipline.
            Here&apos;s what we&apos;ve learned that holds across categories, and what
            generalists keep missing.
          </SectionHeader>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {whyPoints.map((p, i) => (
              <div
                key={i}
                style={{
                  padding: "28px",
                  background: "#fff",
                  border: "1px solid rgba(0,47,167,0.12)",
                  borderRadius: 4,
                }}
              >
                <div
                  className="mono mb-4"
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    color: "rgba(0,47,167,0.45)",
                  }}
                >
                  {p.tag}
                </div>
                <h3
                  style={{
                    fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                    fontWeight: 300,
                    fontSize: 22,
                    letterSpacing: "-0.01em",
                    margin: 0,
                    color: "#002FA7",
                    lineHeight: 1.25,
                  }}
                >
                  {p.title}
                </h3>
                <p
                  className="prose-small mt-3"
                  style={{ color: "rgba(0,47,167,0.74)", fontSize: 14.5, lineHeight: 1.55 }}
                >
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </SectionLayout>
      </section>

      <section className="bhg-section">
        <SectionLayout>
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <SectionHeader num="03, Origin" title={<>Scottsdale, <span className="display-italic">est. 2021.</span></>} />
              <p
                className="mt-6"
                style={{ color: "rgba(0,47,167,0.74)", fontSize: 16, lineHeight: 1.6, maxWidth: "52ch" }}
              >
                Founded in 2021 in Scottsdale, AZ, we serve the Phoenix metro and beyond, running accounts in English and Spanish across the US, with bilingual creative where it moves cost-per-lead.
              </p>
            </div>
            <div>
              <SectionHeader num="04, Reach" title={<span className="display-italic">US.</span>} />
              <ul
                className="mt-8 list-none p-0"
              >
                {[
                  ["Headquarters", "Scottsdale, AZ"],
                  ["Founded", "2021"],
                  ["Languages", "English · Español"],
                  ["Markets", "United States"],
                ].map(([l, v]) => (
                  <li
                    key={l}
                    className="grid items-baseline gap-6"
                    style={{
                      gridTemplateColumns: "140px 1fr",
                      padding: "16px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                    }}
                  >
                    <span
                      className="mono"
                      style={{
                        fontSize: 10.5,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "rgba(0,47,167,0.45)",
                      }}
                    >
                      {l}
                    </span>
                    <span style={{ fontSize: 15, color: "#002FA7" }}>{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SectionLayout>
      </section>

      <PartnerBadges />

      <FAQSection items={ABOUT_FAQS} num="FAQ" title={<>Working <span className="display-italic">with us.</span></>} />

      <ContactCTA />
    </SiteShell>
  );
}

const whyPoints = [
  {
    tag: "01, Lead quality > lead volume",
    title: "Margins live in qualified leads, not form fills.",
    body: "A $200 CPL on a job that closes at $25K beats a $40 CPL on tire-kickers. We optimize against booked work and revenue, not raw form completions.",
  },
  {
    tag: "02, Long sales cycles",
    title: "Decisions take weeks, not minutes.",
    body: "High-consideration buyers research, get multiple quotes, and convert when they're ready. Retargeting windows and offline conversion imports have to reflect that: most generalists never set them up.",
  },
  {
    tag: "03, Geographic discipline",
    title: "Your real market is the audience.",
    body: "Service radius, route density, and ICP geography define the audience, not city boundaries or platform defaults. LSAs, PMax, and Meta lookalikes all live or die by this.",
  },
  {
    tag: "04, Seasonality is real",
    title: "Spend has to match demand cycles.",
    body: "Pool openings, exterior painting weather windows, storm response, holiday retail: flat monthly spend ignores the calendar. We don't.",
  },
  {
    tag: "05, Bilingual matters",
    title: "EN / ES across US markets.",
    body: "In many markets, Spanish-speaking customers are an underserved segment with measurably lower acquisition cost. We run native creative, not translated.",
  },
  {
    tag: "06, Platforms reward specialists",
    title: "Partner status is real leverage.",
    body: "Google Partner and Meta Business Partner certifications come with direct platform support, beta access, and faster escalation: leverage that comes from concentrated, disciplined spend.",
  },
];
